import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "helpers/axios";
import { errorMessage } from "helpers/common";
import { toast } from "helpers/toast";
import * as Sentry from "@sentry/react";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { Button, Field, Input } from "components/ui";

const SCENARIO_SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const slugify = (value) => {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
};

const buildDefaultSlug = (settingName, balancedScorecardHashId) => {
  const baseSlug = slugify(settingName || "balanced-scorecard-scenario") || "balanced-scorecard-scenario";
  const suffix = String(balancedScorecardHashId || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .slice(-6);

  return suffix ? `${baseSlug}-${suffix}` : baseSlug;
};

const buildDefaultName = (settingName) => {
  return settingName || "Balanced scorecard scenario";
};

export default function ScenarioExportAction({
  isInternalTenant,
  balancedScorecardHashId,
  settingName,
}) {
  const { translate, editableTranslate } = useTranslation();

  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [slugOverridden, setSlugOverridden] = useState(false);
  const [formValues, setFormValues] = useState({
    scenario_slug: "",
    name: "",
    description: "",
  });

  const buttonId = useMemo(() => {
    return `balanced-scorecard-scenario-export-${balancedScorecardHashId}`;
  }, [balancedScorecardHashId]);

  const resetState = () => {
    const defaultName = buildDefaultName(settingName);

    setError(null);
    setResult(null);
    setLoading(false);
    setSlugOverridden(false);
    setFormValues({
      scenario_slug: buildDefaultSlug(defaultName, balancedScorecardHashId),
      name: defaultName,
      description: "",
    });
  };

  useEffect(() => {
    if (!showModal) return undefined;

    document.body.style.overflowY = "hidden";

    return () => {
      document.body.style.overflowY = "auto";
    };
  }, [showModal]);

  if (!isInternalTenant || !balancedScorecardHashId) {
    return null;
  }

  const handleOpen = (e) => {
    e?.preventDefault();
    resetState();
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setError(null);
    setResult(null);
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      setFormValues((current) => ({
        ...current,
        name: value,
        scenario_slug: slugOverridden ? current.scenario_slug : buildDefaultSlug(value, balancedScorecardHashId),
      }));
      return;
    }

    if (name === "scenario_slug") {
      setSlugOverridden(true);
    }

    setFormValues((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const scenarioName = formValues.name.trim();
    const slug = formValues.scenario_slug.trim();

    if (!scenarioName) {
      setError({
        message: translate("employee_management.scenario_export.scenario_required", "Szenarioname ist erforderlich."),
      });
      return;
    }

    if (!slug) {
      setError({
        message: translate("employee_management.scenario_export.scenario_slug_required", "Szenario-Slug ist erforderlich."),
      });
      return;
    }

    if (!SCENARIO_SLUG_REGEX.test(slug)) {
      setError({
        message: translate("employee_management.scenario_export.scenario_slug_validation", "Der Szenario-Slug darf nur Kleinbuchstaben, Zahlen und Bindestriche enthalten."),
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, status } = await api.post("/exports/data-seeding-scenarios/balanced-scorecard", {
        balanced_scorecard_hash_id: balancedScorecardHashId,
        scenario_slug: slug,
        name: scenarioName,
        description: formValues.description.trim() || null,
      });

      if (status === 200) {
        setResult(data?.data || null);
        toast.success(data?.message || translate("employee_management.scenario_export.success", "Export erfolgreich"));
      }
    } catch (err) {
      setError(errorMessage(err, "export balanced scorecard scenario"));
      Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  };

  const summary = result?.summary || {};
  const catalogSummary = summary?.catalog || {};
  const employeeContextSummary = summary?.employee_context_template || {};
  const balancedScorecardSummary = summary?.balanced_scorecard || {};
  const portalTarget = typeof document !== "undefined" ? document.body : null;

  return (
    <>
      <Button
        variant="ghost" size="inline"
        icon="bi-box-arrow-up"
        id={buttonId}
        onClick={handleOpen}
        data-testid="export-button"
      />
      <ReactTooltip
        anchorSelect={`#${buttonId}`}
        place="bottom"
        content={translate("employee_management.export_balanced_scorecard_scenario", "Szenario exportieren")}
      />

      {showModal && portalTarget && createPortal(
        <>
          <div className={`modal ${showModal ? "fade show d-block" : ""}`} tabIndex="-1" data-testid="modal-modal">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header">
                    <div>
                      <h5 className="modal-title mb-1">
                        {editableTranslate("employee_management.export_balanced_scorecard_scenario", "Szenario exportieren")}
                      </h5>
                      <div className="small text-muted">
                        {settingName || translate("employee.balanced_scorecard", "Leistungskarte")}
                      </div>
                    </div>
                    <Button variant="ghost" size="inline" onClick={handleClose} data-testid="close-button">✕</Button>
                  </div>

                  <div className="modal-body">
                    {error && (
                      <div className="mb-3">
                        <ErrorAlert error={error} onClose={() => setError(null)} />
                      </div>
                    )}

                    <div className="alert alert-info mb-4" role="alert">
                      <div className="d-flex align-items-start">
                        <i className="bi bi-info-circle-fill mr-2 mt-1"></i>
                        <div>
                          <h6 className="font-weight-bold mb-2">
                            {editableTranslate("employee_management.export_balanced_scorecard_scenario.info_title", "Was passiert beim Export?")}
                          </h6>
                          <p className="mb-2">
                            {editableTranslate("employee_management.export_balanced_scorecard_scenario.info_body", "Damit wird die aktuelle Leistungskarte als wiederverwendbares Szenario gespeichert. Sie können sie jetzt als Referenz aufbewahren und später verwenden, um ähnliche Daten erneut einzurichten, ohne von Grund auf neu beginnen zu müssen.")}
                          </p>
                          <ul className="mb-0 pl-3">
                            <li>
                              {editableTranslate("employee_management.export_balanced_scorecard_scenario.info_why", "Exportieren Sie sie, wenn Sie ein gutes Beispiel aufbewahren, Demo- oder Testdaten vorbereiten oder diese Konfiguration später wiederverwenden möchten.")}
                            </li>
                            <li>
                              {editableTranslate("employee_management.export_balanced_scorecard_scenario.info_later", "Später können Sie dieses Szenario während des Anlegevorgangs auswählen, um dieselbe Art von Konfiguration für andere Beschäftigte erneut zu erstellen.")}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group col-md-6">
                        <Field label={editableTranslate("field.name", "Name")}>
                          <Input
                            type="text"
                            name="name"
                            value={formValues.name}
                            onChange={handleChange}
                            maxLength={255}
                            autoComplete="off" data-testid="name-input"
                          />
                        </Field>
                        <small className="form-text text-muted">
                          {editableTranslate("employee_management.export_balanced_scorecard_scenario.name_hint", "Wählen Sie einen eindeutigen Namen, damit Sie diesen Export später wiedererkennen können.")}
                        </small>
                      </div>
                      <div className="form-group col-md-6">
                        <Field label={editableTranslate("field.scenario_slug", "Szenario-Slug")}>
                          <Input
                            type="text"
                            name="scenario_slug"
                            value={formValues.scenario_slug}
                            onChange={handleChange}
                            maxLength={100}
                            placeholder="regelbeurteilung-abc123"
                            autoComplete="off" data-testid="scenario-slug-input"
                          />
                        </Field>
                        <small
                          className="form-text text-muted d-block"
                          style={{ whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "break-word" }}
                        >
                          {editableTranslate("employee_management.export_balanced_scorecard_scenario.slug_hint", "Dieses kurze Label wird automatisch aus dem Namen erstellt. Ändern Sie es nur, wenn Sie ein anderes verwenden möchten.")}
                        </small>
                      </div>
                    </div>

                    <div className="form-group mb-0">
                      <Field label={editableTranslate("field.description", "Beschreibung")} htmlFor="scenario-export-description">
                        <textarea
                          id="scenario-export-description"
                          name="description"
                          className="form-control"
                          rows="3"
                          value={formValues.description}
                          onChange={handleChange}
                          maxLength={1000} data-testid="description-input"
                        />
                      </Field>
                    </div>

                    {result && (
                      <div className="mt-4 border rounded p-3 bg-light">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0 font-weight-bold">
                            {editableTranslate("employee_management.export_balanced_scorecard_scenario.result", "Exportergebnis")}
                          </h6>
                          <span className="badge badge-success">
                            {editableTranslate("global.success", "Erfolg")}
                          </span>
                        </div>

                        <div className="row small">
                          <div className="col-md-6 mb-2">
                            <strong>{editableTranslate("field.scenario_slug", "Szenario-Slug")}:</strong> {result?.slug || "-"}
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>{editableTranslate("field.schema_version", "Schemaversion")}:</strong> {result?.schema_version || "-"}
                          </div>
                          <div className="col-md-12 mb-2 text-break">
                            <strong>{editableTranslate("field.checksum", "Prüfsumme")}:</strong> {result?.checksum || "-"}
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>{editableTranslate("employee_management.export_balanced_scorecard_scenario.remote_copy", "Remote-Kopie")}:</strong> {result?.has_remote_copy ? editableTranslate("form.yes", "Ja") : editableTranslate("form.no", "nein")}
                          </div>
                          <div className="col-md-6 mb-2 text-break">
                            <strong>{editableTranslate("field.remote_disk", "Remote-Speicher")}:</strong> {result?.remote_disk || "-"}
                          </div>
                        </div>

                        <hr />

                        <div className="row small">
                          <div className="col-md-12 mb-2">
                            <strong>{editableTranslate("employee_management.export_balanced_scorecard_scenario.catalog_sections", "Katalogabschnitte")}:</strong>{" "}
                            {(catalogSummary?.available_sections || []).join(", ") || "-"}
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>{editableTranslate("employee_management.export_balanced_scorecard_scenario.interview_settings", "Einstellungsgespräch-Einstellungen")}:</strong>{" "}
                            {catalogSummary?.interview_settings_count ?? "-"}
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>{editableTranslate("employee_management.export_balanced_scorecard_scenario.employee_information", "Zeilen mit Beschäftigteninformationen")}:</strong>{" "}
                            {employeeContextSummary?.employee_information_count ?? "-"}
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>{editableTranslate("employee_management.export_balanced_scorecard_scenario.employee_period_data", "Periodendaten der Beschäftigten")}:</strong>{" "}
                            {employeeContextSummary?.has_employee_period_data ? editableTranslate("form.yes", "Ja") : editableTranslate("form.no", "nein")}
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>{editableTranslate("employee_management.export_balanced_scorecard_scenario.completion_state", "Abschlussstatus")}:</strong>{" "}
                            {balancedScorecardSummary?.has_completion_state ? editableTranslate("form.yes", "Ja") : editableTranslate("form.no", "nein")}
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>{editableTranslate("employee_management.export_balanced_scorecard_scenario.individual_objectives", "Individuelle Ziele")}:</strong>{" "}
                            {balancedScorecardSummary?.individual_objectives_count ?? "-"}
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>{editableTranslate("employee_management.export_balanced_scorecard_scenario.team_objectives", "Teamziele")}:</strong>{" "}
                            {balancedScorecardSummary?.team_objectives_count ?? "-"}
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>{editableTranslate("employee_management.export_balanced_scorecard_scenario.team_assignments", "Zuweisungen von Teamzielen")}:</strong>{" "}
                            {balancedScorecardSummary?.team_objective_assignments_count ?? "-"}
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>{editableTranslate("employee_management.export_balanced_scorecard_scenario.employee_interviews", "Gespräche mit Beschäftigten")}:</strong>{" "}
                            {balancedScorecardSummary?.employee_interviews_count ?? "-"}
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>{editableTranslate("employee_management.export_balanced_scorecard_scenario.interview_statuses", "Gesprächsstatus")}:</strong>{" "}
                            {balancedScorecardSummary?.interview_statuses_count ?? "-"}
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>{editableTranslate("employee_management.export_balanced_scorecard_scenario.complaints", "Beschwerden")}:</strong>{" "}
                            {balancedScorecardSummary?.complaints_count ?? "-"}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="modal-footer">
                    <Button variant="secondary" onClick={handleClose} data-testid="close-button">
                      {editableTranslate("button.close", "Schließen")}
                    </Button>
                    <Button type="submit" variant="primary" loading={loading} data-testid="export-button">{editableTranslate("global.export", "Export")}</Button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>,
        portalTarget
      )}
    </>
  );
}
